import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.textPrimary};
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, rgba(62, 207, 142, 0.18), transparent 55%),
    radial-gradient(ellipse 50% 40% at 100% 100%, rgba(62, 207, 142, 0.08), transparent 50%),
    ${({ theme }) => theme.colors.main};
`

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  padding: 40px 32px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: 0 16px 40px rgba(15, 23, 42, ${({ theme }) => (theme.name === 'dark' ? 0.35 : 0.08)});
  text-align: center;
  animation: ${fadeUp} 0.45s ease-out both;

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

export const BrandName = styled.span`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Code = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.action.primary.bgActive};
  animation: ${fadeUp} 0.45s 0.05s ease-out both;
`

export const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
  animation: ${fadeUp} 0.45s 0.1s ease-out both;
`

export const Description = styled.p`
  margin: 0 0 1.75rem;
  max-width: 34ch;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  animation: ${fadeUp} 0.45s 0.15s ease-out both;
`

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  animation: ${fadeUp} 0.45s 0.2s ease-out both;
`

const primaryActionStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
`

export const PrimaryLink = styled(Link)`
  ${primaryActionStyles}
  background: ${({ theme }) => theme.colors.action.primary.bg};
  color: ${({ theme }) => theme.colors.action.primary.text};

  &:hover {
    background: ${({ theme }) => theme.colors.action.primary.bgHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

export const PrimaryButton = styled.button`
  ${primaryActionStyles}
  background: ${({ theme }) => theme.colors.action.primary.bg};
  color: ${({ theme }) => theme.colors.action.primary.text};

  &:hover {
    background: ${({ theme }) => theme.colors.action.primary.bgHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

export const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark' ? theme.colors.backgroundSecondary : theme.colors.borderSubtle};
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
  }
`
